import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("ci-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("ci-chat-session", id);
  }
  return id;
};

const ChatWidget = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.chat.greeting }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text, lang }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.token };
                return copy;
              });
            }
            if (parsed.error) throw new Error("stream error");
          } catch (e) {
            if (e.message === "stream error") throw e;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t.chat.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(true)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2.2, type: "spring", stiffness: 200 }}
        className={`fixed bottom-6 right-6 z-40 p-4 rounded-full bg-[#0D253A] text-[#F8F5F0] shadow-[0_15px_40px_-10px_rgba(13,37,58,0.5)] hover:bg-[#C86D51] transition-colors duration-300 ${open ? "hidden" : ""}`}
        aria-label={t.chat.open}
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm h-[520px] bg-white rounded-3xl border border-[#0D253A]/10 shadow-[0_30px_80px_-20px_rgba(13,37,58,0.45)] flex flex-col overflow-hidden"
          >
            <div className="bg-[#0D253A] text-[#F8F5F0] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="p-2 rounded-full bg-[#C86D51]">
                  <Sparkles className="w-4 h-4" />
                </span>
                <div>
                  <p className="font-display text-sm font-semibold">{t.chat.title}</p>
                  <p className="text-[10px] text-[#F8F5F0]/60">{t.chat.subtitle}</p>
                </div>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="p-1.5 hover:text-[#C86D51] transition-colors" aria-label="Chiudi chat">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 bg-[#F8F5F0]">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "self-end bg-[#0D253A] text-[#F8F5F0] rounded-br-sm"
                      : "self-start bg-white border border-[#0D253A]/10 text-[#1A2129] rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-[#0D253A]/10 bg-white flex items-center gap-2">
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.chat.placeholder}
                className="flex-1 px-4 py-2.5 rounded-full bg-[#F8F5F0] border border-[#0D253A]/10 text-sm"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={streaming || !input.trim()}
                className="p-3 rounded-full bg-[#C86D51] text-white hover:bg-[#0D253A] disabled:opacity-50 transition-colors"
                aria-label="Invia"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
